import { ReadyState, Store } from './store';
import { makeObservable, observable, action } from 'mobx';
import { BaseStore } from './baseStore';
import { showCustomToast } from '../components/notifications/CustomToast';

export enum FriendStoreState {
  NOT_INITIALIZED = 1 << 0,
  RECEIVED_INCOMING = 1 << 1,
  RECEIVED_OUTGOING = 1 << 2,
  RECEIVED_ACCEPTED = 1 << 3,
}

export class FriendStore extends BaseStore<FriendStoreState> {
  incoming: any[];
  outgoing: any[];
  accepted: any[];
  constructor(store: Store) {
    super(FriendStoreState.NOT_INITIALIZED, store, 'blue');
    makeObservable(this, {
      incoming: observable,
      outgoing: observable,
      accepted: observable,
      setIncoming: action,
      setOutgoing: action,
      setAccepted: action,
    });
    this.incoming = [];
    this.outgoing = [];
    this.accepted = [];
    this.reload();
    this.log('Init FriendStore endpoint');
  }

  reload() {
    this.log('FriendStore reload called - but keeping existing data');
    if (this.store.token == null) return;
    this.store.socket.ws.on('friends:incoming', data => {
      this.log('Received incoming requests from socket:', data);
      this.store!.friends.setIncoming(data);
    });

    this.store.socket.ws.on('friend:request:received', data => {
      this.log('Received new friend request:', data);
      const message = data.senderName
        ? `Otrzymałeś zaproszenie do znajomych od ${data.senderName}!`
        : 'Otrzymałeś nowe zaproszenie do znajomych!';
      showCustomToast(message);
    });

    this.store.socket.ws.on('friend:request:sent', data => {
      this.log('Friend request sent confirmation:', data);
    });

    this.store.socket.ws.on('friend:request:accepted', data => {
      this.log('Friend request accepted:', data);
      const message = data.accepterName
        ? `${data.accepterName} zaakceptował Twoje zaproszenie do znajomych!`
        : 'Twoje zaproszenie zostało zaakceptowane!';
      showCustomToast(message);
    });

    this.store.socket.ws.on('friend:request:declined', data => {
      this.log('Friend request declined:', data);
      const message = data.declinerName
        ? `${data.declinerName} odrzucił Twoje zaproszenie do znajomych.`
        : 'Twoje zaproszenie zostało odrzucone.';
      showCustomToast(message);
    });

    this.store.socket.ws.on('friend:cancel', data => {
      this.log('Friend request cancelled:', data);
    });

    this.store.socket.ws.on('friends:outgoing', data => {
      this.log('Received outgoing requests from socket');
      this.store!.friends.setOutgoing(data);
    });

    this.store.socket.ws.on('friends:accepted', data => {
      this.log('Received accepted requests from socket');
      this.store!.friends.setAccepted(data);
    });
    this.setState(FriendStoreState.NOT_INITIALIZED);
  }

  async loadFriends() {
    try {
      this.log('Loading friends data at', new Date().toISOString());

      if (!this.store?.token) {
        this.log('No token available, cannot load friends');
        return;
      }

      const [incomingRes, outgoingRes, acceptedRes] = await Promise.all([
        fetch(`${this.store.apiURL}/user/@me/friends/incoming`, {
          headers: { Authorization: `Bearer ${this.store.token}` },
        }),
        fetch(`${this.store.apiURL}/user/@me/friends/outgoing`, {
          headers: { Authorization: `Bearer ${this.store.token}` },
        }),
        fetch(`${this.store.apiURL}/user/@me/friends/accepted`, {
          headers: { Authorization: `Bearer ${this.store.token}` },
        }),
      ]);

      if (incomingRes.ok) {
        const incomingData = await incomingRes.json();
        const data = incomingData?.data || incomingData || [];
        this.setIncoming(Array.isArray(data) ? data : []);
      } else {
        this.log(
          '❌ [FRIENDS] Incoming request failed:',
          incomingRes.status,
          incomingRes.statusText,
        );
      }

      if (outgoingRes.ok) {
        const outgoingData = await outgoingRes.json();
        this.log('Raw outgoing data:', outgoingData);
        // Handle null data - convert to empty array
        const data = outgoingData?.data || outgoingData || [];
        this.setOutgoing(Array.isArray(data) ? data : []);
      } else {
        this.log('Outgoing request failed:', outgoingRes.status);
      }

      if (acceptedRes.ok) {
        const acceptedData = await acceptedRes.json();
        const data = acceptedData?.data || acceptedData || [];
        this.setAccepted(Array.isArray(data) ? data : []);
      } else {
        this.log('Accepted request failed:', acceptedRes.status);
      }

      this.log('Friends data loaded successfully');
    } catch (error) {
      this.log('Error loading friends:', error);
    }
  }

  async searchUser(query: string) {
    if (!this.store?.token) {
      throw new Error('No token available');
    }

    const res = await fetch(
      `${this.store.apiURL}/user/search?q=${encodeURIComponent(query.trim())}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${this.store.token}` },
      },
    );

    if (!res.ok) {
      const errorData = await res.json().catch(() => null);
      if (res.status === 400) {
        throw new Error(errorData?.message || 'Nieprawidłowe zapytanie');
      }
      throw new Error('Błąd podczas wyszukiwania użytkownika');
    }

    const data = await res.json();
    if (!data?.data?.users?.length) {
      throw new Error('Nie znaleziono użytkownika');
    }

    return data.data.users[0];
  }

  async sendFriendRequest(userId: number) {
    if (!this.store?.token) {
      throw new Error('No token available');
    }

    const res = await fetch(`${this.store.apiURL}/user/@me/friends/invite/${userId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.store.token}` },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => null);
      if (res.status === 409) {
        throw new Error('Zaproszenie już zostało wysłane lub jesteście znajomymi');
      } else if (res.status === 404) {
        throw new Error('Nie ma takiego użytkownika');
      }
      throw new Error(errorData?.message || 'Błąd wysyłania zaproszenia');
    }

    return true;
  }

  setIncoming(requests: any[]) {
    this.log('🔔 [FRIENDS] Incoming request will be received', requests);
    this.log(
      '🔔 [FRIENDS] Setting incoming from:',
      Array.isArray(requests) ? `array with ${requests.length} items` : typeof requests,
    );
    // Handle null/undefined - convert to empty array
    this.incoming = Array.isArray(requests) ? requests : [];
    if (!this.hasState(FriendStoreState.RECEIVED_INCOMING))
      this.addState(FriendStoreState.RECEIVED_INCOMING);
    this.tryUpdateGlobalState();
  }

  setOutgoing(requests: any[]) {
    this.log('Outgoing request will be received', requests);
    this.log(
      'Setting outgoing from:',
      Array.isArray(requests) ? `array with ${requests.length} items` : typeof requests,
    );
    // Handle null/undefined - convert to empty array
    this.outgoing = Array.isArray(requests) ? requests : [];
    if (!this.hasState(FriendStoreState.RECEIVED_OUTGOING))
      this.addState(FriendStoreState.RECEIVED_OUTGOING);
    this.tryUpdateGlobalState();
  }

  setAccepted(requests: any[]) {
    this.log('Accepted request will be received', requests);
    this.log(
      'Setting accepted from:',
      Array.isArray(requests) ? `array with ${requests.length} items` : typeof requests,
    );
    // Handle null/undefined - convert to empty array
    this.accepted = Array.isArray(requests) ? requests : [];
    if (!this.hasState(FriendStoreState.RECEIVED_ACCEPTED))
      this.addState(FriendStoreState.RECEIVED_ACCEPTED);
    this.tryUpdateGlobalState();
  }

  isReady() {
    return (
      this.hasState(FriendStoreState.RECEIVED_INCOMING) &&
      this.hasState(FriendStoreState.RECEIVED_OUTGOING) &&
      this.hasState(FriendStoreState.RECEIVED_ACCEPTED)
    );
  }

  tryUpdateGlobalState() {
    if (this.isReady() && !this.store!.hasState(ReadyState.USER_RELATIONS))
      this.store!.addState(ReadyState.USER_RELATIONS);
  }

  logout() {
    this.incoming = [];
    this.outgoing = [];
    this.accepted = [];
    this.setState(FriendStoreState.NOT_INITIALIZED);
  }
}
